/**
 * `usePageTitle(name)` — name the browser tab after what is open.
 *
 * The SPA shipped one static `<title>`, rewritten at BUILD time with the
 * brand's root title and never touched again, so every tab in a window read
 * identically no matter what was in it. This is the runtime half:
 * `formatPageTitle` and `identity.pageTitleTemplate` were already in place and
 * had no caller.
 *
 * ── Why a STACK and not just `document.title = …` ────────────────────────
 *
 * More than one surface is mounted at once. The shell keeps windows alive
 * behind the active one (a workspace stays mounted while you look at the
 * drive), a modal or a file viewer nests inside a page that has already named
 * itself, and React unmounts children before parents. A hook that wrote on
 * mount and restored the ROOT title on unmount would therefore blank a title
 * its parent still owns, and two live surfaces would race on who wrote last.
 *
 * So each caller holds an entry and the LAST ACTIVE one wins. Dropping an
 * entry re-applies whatever is beneath it — the parent's name, or the root
 * title when the stack empties. Nothing has to know what else is mounted.
 *
 * `active: false` keeps the entry's place in the stack while it is not the
 * visible surface, which is what a hidden-but-mounted shell window needs: it
 * must not name the tab, and it must not lose its position either.
 *
 * ── The name is DATA, and it is user data ────────────────────────────────
 *
 * A tab title is copied into the browser's history, its tab strip, session
 * restore and (on some platforms) the window manager — places outside this
 * app's control and outside its permission model. That is the ordinary
 * bargain every document editor makes, and the names used here are the ones
 * already on screen. But it is why a caller passes a NAME rather than a
 * template: interpolation happens here, from brand config, so no caller can
 * put anything else in the title.
 */
import { useEffect, useId } from 'react'
import { getBrandSnapshot } from '../client.js'
import { formatPageTitle, formatRootTitle } from '../page-title.js'

interface Entry {
  id: string
  title: string | null
}

const stack: Entry[] = []

/** Apply the topmost non-null entry, or the brand's root title. */
function apply(): void {
  if (typeof document === 'undefined') return
  const brand = getBrandSnapshot()
  for (let i = stack.length - 1; i >= 0; i--) {
    const t = stack[i]!.title
    if (t) { document.title = formatPageTitle(brand, t); return }
  }
  document.title = formatRootTitle(brand)
}

/**
 * Name the tab after `name` while this component is mounted and `active`.
 *
 * Pass `null`/`undefined` (or `active: false`) while the name is still
 * loading: the tab keeps whatever it had rather than flashing the root title
 * and then the real one. Every surface resolves its name asynchronously, so
 * that is the common case, not an edge one.
 */
export function usePageTitle(name: string | null | undefined, active = true): void {
  const id = useId()
  const title = active ? (name?.trim() || null) : null

  useEffect(() => {
    const existing = stack.find((e) => e.id === id)
    if (existing) existing.title = title
    else stack.push({ id, title })
    apply()
  }, [id, title])

  // Separate from the write above ON PURPOSE: this must run only when the
  // component goes away, and folding it into the effect above would pop the
  // entry on every name change and push it back at the TOP of the stack,
  // silently promoting a background surface over the one in front.
  useEffect(() => () => {
    const i = stack.findIndex((e) => e.id === id)
    if (i >= 0) stack.splice(i, 1)
    apply()
  }, [id])
}
